import type { NavigationItem, NavigationItemLinkType } from '@/types/aggregation/common';
import type { SiteWideJackpotData } from '@/types/aggregation/site-wide-jackpot';
import { NAVIGATION_ITEM_TYPE_LIVESTREAM } from '@/utils/constants/navigation';
import { PRODUCT_THROTTLES } from '@/utils/feature-flags/constants';
import { removeProductFromUid } from '@/utils/url/url';
import { NEW_CATEGORY_UIDS } from '@/app/(pages)/(category)/c/[slug]/constants';
import { getPathFromNavigationItem } from './paths';

export type NavigationItemUI = {
    name: string;
    pathname: string | null;
    customImage: {
        name: string;
        url: string;
    };
    customImageActive: {
        name: string;
        url: string;
    };
    type?: string;
    linkUID?: string;
    linkType?: NavigationItemLinkType;
    isHighlighted?: boolean;
    newItemsCount?: number;
    pinned: boolean;
    isPersonalized: boolean;
};

export const adaptNavigationItems = (items: NavigationItem[], isPersonalizedNav = false): NavigationItemUI[] => {
    return items
        .map((item) => ({
            name: item.name,
            pathname: getPathFromNavigationItem(item),
            linkUID: item.link?.uid,
            linkType: item.link?.type,
            isHighlighted: item.is_highlighted,
            type: item.item_type,
            customImage: {
                name: item.custom_image?.name ?? '',
                url: item.custom_image?.url ?? ''
            },
            customImageActive: {
                name: item.custom_image_active?.name ?? '',
                url: item.custom_image_active?.url ?? ''
            },
            newItemsCount: item.new_items_count,
            pinned: Boolean(item.pinned),
            isPersonalized: isPersonalizedNav
        }))
        .filter((link) => link.pathname)
        .filter((link, index, links) => links.findIndex((candidate) => candidate.pathname === link.pathname) === index);
};

export const adaptTopNavigation = (
    items: NavigationItem[],
    siteWideJackpot: SiteWideJackpotData | undefined,
    isPersonalizedNav = false,
    productFeatureNames: string[] = [],
    bottomBarItems: NavigationItem[] = []
): NavigationItemUI[] => {
    const mappedItems = adaptNavigationItems(items, isPersonalizedNav);
    const deduplicatedItems = mappedItems.filter((item) => {
        const uid = item.linkUID ?? '';
        return (
            NEW_CATEGORY_UIDS.includes(uid) ||
            uid === 'casino-refer' ||
            !bottomBarItems.some(
                (bottomItem) =>
                    bottomItem.link.uid === uid ||
                    (item.pathname !== null && getPathFromNavigationItem(bottomItem) === item.pathname)
            )
        );
    });
    const hasLivestream = productFeatureNames.includes(PRODUCT_THROTTLES.HAS_LIVESTREAM);
    const visibleItems = hasLivestream
        ? deduplicatedItems
        : deduplicatedItems.filter((item) => item.type?.toLowerCase() !== NAVIGATION_ITEM_TYPE_LIVESTREAM);

    if (siteWideJackpot?.enabled === true) {
        return visibleItems;
    }

    const jackpotCategoryUids = new Set(
        (siteWideJackpot?.jackpot_categories ?? [])
            .map((item: { category?: { uid?: string }; uid?: string }) => item.category?.uid ?? item.uid)
            .filter((uid): uid is string => Boolean(uid))
            .map((uid) => removeProductFromUid(uid))
    );

    if (jackpotCategoryUids.size === 0) {
        return visibleItems;
    }

    return visibleItems.filter((item) => {
        if (item.linkType !== 'category' || !item.linkUID) {
            return true;
        }
        return !jackpotCategoryUids.has(removeProductFromUid(item.linkUID));
    });
};
